// Requests to the Go service's routes (/updates, /fetch-events, /graphql, ...), resolving to
// the same { status, data, errors } shape as @core's window.$ajaxGet/$ajaxPost/$ajaxGraphql.
// In the browser those helpers are used as-is (cookies, access-token refresh); on the server
// the Go service is called directly on behalf of the visitor's tenant.
export interface ApiResult {
    status: number
    data: any
    errors: any[]
}

export interface Api {
    get: (path: string) => Promise<ApiResult>
    post: (path: string, body: any) => Promise<ApiResult>
    graphql: (query: string, variables?: Record<string, any>, path?: string) => Promise<ApiResult>
}

export default defineNuxtPlugin(() => {
    if (import.meta.client) {
        const api: Api = {
            get: path => window.$ajaxGet(path),
            post: (path, body) => window.$ajaxPost(path, body),
            graphql: (query, variables = {}, path = '/graphql') => window.$ajaxGraphql(query, variables, path),
        }

        return { provide: { api } }
    }

    const { apiBase } = useRuntimeConfig()
    const url = useRequestURL({ xForwardedHost: true })
    const headers = {
        ...useRequestHeaders(['cookie', 'user-agent', 'accept-language', 'x-forwarded-for']),
        'accept': 'application/json',
        // The Go service resolves the tenant from Origin + X-Forwarded-Proto (ClientMiddleware).
        'origin': url.origin,
        'x-forwarded-proto': url.protocol.replace(':', ''),
        // Tells the Go catch-all not to proxy an unknown route back to this server.
        'x-nuxt-ssr': '1',
    }

    const request = async (path: string, init: { method?: 'GET' | 'POST', body?: any } = {}): Promise<ApiResult> => {
        try {
            const res = await $fetch.raw(path, { baseURL: apiBase, headers, ignoreResponseError: true, ...init })
            const json: any = res._data

            if (json && Array.isArray(json.errors) && json.errors.length) {
                return { status: res.status, data: json.data || null, errors: json.errors }
            }

            return { status: res.status, data: json?.data || json, errors: [] }
        } catch (error: any) {
            return { status: 502, data: null, errors: [{ message: error?.message || 'Network request failed' }] }
        }
    }

    const api: Api = {
        get: path => request(path),
        post: (path, body) => request(path, { method: 'POST', body }),
        graphql: (query, variables = {}, path = '/graphql') => request(path, { method: 'POST', body: { query, variables } }),
    }

    return { provide: { api } }
})
